import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, ScrollText, ShieldCheck } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useSiteData } from "@/hooks/useSite";
import { photoPosition } from "@/lib/site";
import { LineReveal, Reveal, ParallaxImage, FadeIn } from "@/components/Motion";

const isPlaceholder = (t) => !t || t.trim().startsWith("[");

function PlaceholderBadge() {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest2 font-medium text-deeptaupe bg-peach border border-taupe/30 rounded-full px-3 py-1"
      data-testid="placeholder-badge"
    >
      <ScrollText size={11} /> Contenuto da completare
    </span>
  );
}

export default function About() {
  const { settings: seoSettings } = useSiteData();
  const seo = seoSettings.seo?.["chi-sono"] || {};
  useSEO({
    title: seo.title || "Chi sono — Dott.ssa Francesca Notarfrancesco | K Nutrition Salerno",
    description:
      seo.description ||
      "Piacere, Francesca. Dott.ssa Francesca Notarfrancesco, Biologa Nutrizionista a Salerno: ascolto, personalizzazione e abitudini sostenibili per un percorso nutrizionale che dura nel tempo.",
    path: "/chi-sono",
  });

  const { settings } = useSiteData();
  const a = settings.content.about;
  const c = settings.contact;

  const credentials = [
    { icon: GraduationCap, label: a.educationTitle || "Formazione universitaria", text: a.education },
    { icon: ScrollText, label: a.trainingTitle || "Aggiornamento professionale", text: a.training },
    { icon: ShieldCheck, label: a.registrationTitle || "Iscrizione all'ordine professionale", text: a.registration },
  ];

  return (
    <>
      {/* hero */}
      <section className="pt-28 lg:pt-36 pb-12 lg:pb-16 overflow-hidden" data-testid="about-hero">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12">
          <FadeIn>
            <p className="flex items-center gap-3 text-[11px] uppercase tracking-widest2 text-deeptaupe font-medium">
              <span className="w-10 h-px bg-taupe inline-block" aria-hidden="true" />
              {a.aboutEyebrow || "Chi sono"}
            </p>
          </FadeIn>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight mt-6">
            <LineReveal delay={0.2}>{a.title.split(" ")[0]}</LineReveal>
            <LineReveal delay={0.4} className="italic text-deeptaupe">
              {a.title.split(" ").slice(1).join(" ")}
            </LineReveal>
          </h1>
          <FadeIn delay={0.7}>
            <p className="text-base lg:text-lg leading-relaxed text-charcoal/75 max-w-xl mt-6 mb-10 lg:mb-14">{a.lead}</p>
          </FadeIn>
        </div>
        <FadeIn delay={0.25}>
          <div className="w-full aspect-[16/9] lg:aspect-[21/9] overflow-hidden" data-testid="about-cover-band">
            <ParallaxImage
              src={settings.photos.about}
              alt="Dott.ssa Francesca Notarfrancesco con una carota"
              className="w-full h-full"
              priority
              position={photoPosition(settings, "about", "50% 26%")}
            />
          </div>
        </FadeIn>
      </section>

      {/* biography placeholder */}
      <section className="pb-24 lg:pb-32" data-testid="about-biography">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-3">
            <Reveal>
              <p className="text-[11px] uppercase tracking-widest2 text-taupe font-medium lg:sticky lg:top-32">{a.biographyTitle || "Biografia"}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal>
              <div className="border border-taupe/30 bg-peach/40 rounded-3xl p-8 lg:p-12 lg:h-full" data-testid="about-biography-box">
                {isPlaceholder(a.biography) && <PlaceholderBadge />}
                {isPlaceholder(a.biography) ? (
                  <p className="font-display text-2xl lg:text-3xl leading-snug mt-6 text-charcoal/80">{a.biography}</p>
                ) : (
                  <p className="mt-6 space-y-4 text-base lg:text-lg leading-relaxed text-charcoal/80 whitespace-pre-line first:mt-0">{a.biography}</p>
                )}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-4">
            <Reveal delay={0.1}>
              <div className="relative">
                <div aria-hidden="true" className="absolute -top-5 -left-5 w-32 h-32 rounded-full bg-peach -z-10" />
                <div className="overflow-hidden rounded-[24px] aspect-[3/4]" data-testid="about-second-photo">
                  <ParallaxImage
                    src={settings.photos.aboutSecond}
                    alt="Primo piano di Dott.ssa Francesca Notarfrancesco in luce calda"
                    className="w-full h-full"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* credentials placeholders */}
      <section className="pb-24 lg:pb-32" data-testid="about-credentials">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {credentials.map((cred, i) => (
            <Reveal key={cred.label} delay={i * 0.08} className="h-full">
              <div className="h-full bg-white rounded-[28px] p-8 flex flex-col transition-all duration-500 hover:-translate-y-1 hover:shadow-soft">
                <cred.icon size={22} strokeWidth={1.4} className="text-taupe mb-6" />
                <h3 className="font-display text-2xl mb-3">{cred.label}</h3>
                <p className="text-[15px] leading-relaxed text-charcoal/75 whitespace-pre-line">{cred.text}</p>
                {isPlaceholder(cred.text) && (
                  <div className="mt-auto pt-6">
                    <PlaceholderBadge />
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* philosophy */}
      <section className="pb-24 lg:pb-32" data-testid="about-philosophy">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="rounded-[28px] overflow-hidden aspect-[4/5]">
                <ParallaxImage
                  src={settings.photos.philosophy}
                  alt="Dott.ssa Francesca Notarfrancesco al supermercato con l'insalata in mano e una mela"
                  className="w-full h-full"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <p className="text-[11px] uppercase tracking-widest2 text-taupe font-medium mb-4">{a.philosophyTitle}</p>
              <h2 className="font-display text-4xl lg:text-5xl leading-[1.08] tracking-tight mb-8">
                {a.philosophyHeading || "Equilibrio, non regole rigide."}
              </h2>
              <p className="text-base lg:text-lg leading-relaxed text-charcoal/75">{a.philosophyText}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <blockquote className="mt-10 pl-6 border-l-2 border-taupe font-display italic text-2xl lg:text-3xl leading-snug text-deeptaupe">
                "{a.biographyQuote || settings.content.method.statement}"
              </blockquote>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 lg:pb-32">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12">
          <Reveal>
            <div className="bg-peach rounded-[36px] px-8 py-14 lg:px-20 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
              <div>
                <h2 className="font-display text-4xl lg:text-5xl" data-testid="about-cta-title">{a.ctaTitle || "Iniziamo insieme."}</h2>
                <p className="mt-3 text-charcoal/70">
                  Scrivi a {c.email} oppure richiedi il tuo appuntamento direttamente su WhatsApp.
                </p>
              </div>
              <Link
                to="/prenota"
                data-testid="about-cta-button"
                className="group inline-flex items-center gap-3 self-start lg:self-auto rounded-full bg-charcoal text-ivory text-sm uppercase tracking-widest2 font-medium px-10 py-5 transition-all duration-300 hover:-translate-y-0.5 hover:bg-deeptaupe"
              >
                {a.ctaButton || "Prenota una consulenza"}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
